import { GradeFormat } from '@gpa-scales/shared-types';
import { IB_SCALE_MAPPING, ECTS_SCALE_MAPPING } from '../data/gradePointMappings';

/**
 * Convert IB 1–7 grade to US 4.0 scale points.
 * Returns null for invalid grades.
 */
export function convertIBToUSPoints(ibGrade: number): number | null {
  if (!Number.isInteger(ibGrade) || ibGrade < 1 || ibGrade > 7) {
    return null;
  }
  const found = IB_SCALE_MAPPING.find((m) => m.grade === String(ibGrade));
  return found?.points ?? null;
}

/**
 * Convert ECTS grade (A–F) to US 4.0 scale points.
 * Returns null for invalid grades.
 */
export function convertECTSToUSPoints(ectsGrade: string): number | null {
  const normalized = ectsGrade.toUpperCase().trim();
  const found = ECTS_SCALE_MAPPING.find((m) => m.grade === normalized);
  return found?.points ?? null;
}

/**
 * Convert percentage grade to US 4.0 scale points using thresholds.
 * Supports custom thresholds or default US thresholds.
 */
export function convertPercentageToUSPoints(
  pct: number,
  thresholds?: { min: number; letter: string }[]
): number | null {
  if (isNaN(pct) || pct < 0 || pct > 100) return null;

  const thresholdsToUse = thresholds ?? [
    { min: 93, letter: 'A' },
    { min: 90, letter: 'A-' },
    { min: 87, letter: 'B+' },
    { min: 83, letter: 'B' },
    { min: 80, letter: 'B-' },
    { min: 77, letter: 'C+' },
    { min: 73, letter: 'C' },
    { min: 70, letter: 'C-' },
    { min: 67, letter: 'D+' },
    { min: 63, letter: 'D' },
    { min: 60, letter: 'D-' },
    { min: 0, letter: 'F' },
  ];

  const clamped = Math.max(0, Math.min(100, pct));
  let letter = 'F';
  for (const threshold of thresholdsToUse) {
    if (clamped >= threshold.min) {
      letter = threshold.letter;
      break;
    }
  }

  // Map letter to 4.0 scale
  const letterMap: Record<string, number> = {
    'A+': 4.0, 'A': 4.0, 'A-': 3.7,
    'B+': 3.3, 'B': 3.0, 'B-': 2.7,
    'C+': 2.3, 'C': 2.0, 'C-': 1.7,
    'D+': 1.3, 'D': 1.0, 'D-': 0.7,
    'F': 0.0,
  };

  return letterMap[letter] ?? 0;
}

/**
 * Auto-detect grade format from the grade string.
 * Returns the detected GradeFormat or null if indeterminate.
 */
export function detectGradeFormat(grade: string): GradeFormat | null {
  const trimmed = grade.trim();
  if (!trimmed) return null;

  // Check IB: single digit 1–7
  if (/^[1-7]$/.test(trimmed)) {
    return GradeFormat.IB_1_7;
  }

  // Check ECTS: A–F (single uppercase letter)
  if (/^[A-F]$/.test(trimmed.toUpperCase())) {
    // Could be ECTS or letter grade — check if it's ECTS range
    const upper = trimmed.toUpperCase();
    if (['A', 'B', 'C', 'D', 'E', 'F'].includes(upper)) {
      // Could be either — lean toward letter grade for US context
      // Only choose ECTS if it's just A-F without +/- modifiers
      return GradeFormat.Letter;
    }
  }

  // Check letter grade with +/- modifiers
  if (/^[A-F][+-]?$/.test(trimmed.toUpperCase())) {
    return GradeFormat.Letter;
  }

  // Check percentage: 0–100 (integer or decimal)
  const asNum = parseFloat(trimmed);
  if (!isNaN(asNum)) {
    if (asNum > 10 && asNum <= 100) {
      return GradeFormat.Percentage;
    }
    if (asNum >= 0 && asNum <= 5) {
      // Could be numeric (0–4.0 scale) or percentage < 10
      return GradeFormat.Numeric;
    }
  }

  return null;
}
